import { useMemo } from 'react'
import { Stack, Button, Typography, Box, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useUser } from 'store'
import Radio from 'packages/ui/Radio'

import { useFilters, useSelection } from './store'
import useList from './use-event-ssids'
import { getEventStatus } from './helpers'
import { trackEvent } from 'analytics'

const StatusFilter = () => {
  const { t } = useTranslation()
  const [filters, setFilters] = useFilters()

  const handleStatusChange = (status: string) => {
    if (filters.status === status) return

    setFilters(prev => ({ ...prev, status }))
    trackEvent('wifi_management_filter_changed', {
      filter_name: 'status',
      filter_value: status
    })
  }

  return (
    <Stack direction="row" gap={2} alignItems="center">
      <Stack 
        direction="row" 
        gap={1} 
        alignItems="center"
        sx={{ cursor: 'pointer' }}
        onClick={() => handleStatusChange('current_or_future')}
      >
        <Radio
          checked={filters.status === 'current_or_future'}
          onChange={() => {}}
        />
        <Typography 
          sx={{ 
            fontSize: 10, 
            fontWeight: filters.status === 'current_or_future' ? 500 : 400,
            color: '#464646'
          }}
        >
          {t('event_ssids.filters.active_and_upcoming')}
        </Typography>
      </Stack>
      
      <Stack 
        direction="row" 
        gap={1} 
        alignItems="center"
        sx={{ cursor: 'pointer' }}
        onClick={() => handleStatusChange('all')}
      >
        <Radio
          checked={filters.status === 'all'}
          onChange={() => {}}
        />
        <Typography 
          sx={{ 
            fontSize: 10, 
            fontWeight: filters.status === 'all' ? 500 : 400,
            color: '#464646'
          }}
        >
          {t('event_ssids.filters.all')}
        </Typography>
      </Stack>
    </Stack>
  )
}

const Topbar = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [user] = useUser()
  const [, setSelection] = useSelection()
  const { items: allItemsFiltered } = useList()

  const activeCount = useMemo(() => {
    return allItemsFiltered.filter(item => {
      const { isActive } = getEventStatus(item)
      return isActive
    }).length
  }, [allItemsFiltered])

  const maxActiveSSIDs = 2

  return (
    <Stack gap={3}>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <i className="fa-regular fa-calendar-star" style={{ fontSize: 14, color: '#2570EB' }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#464646' }}>
            {t('event_ssids.title')}
          </Typography>
          <Tooltip title={t('event_ssids.info_tooltip')} arrow>
            <i className="fa-solid fa-circle-info" style={{ fontSize: 12, color: '#656565', cursor: 'pointer' }} />
          </Tooltip>
          <Box
            sx={{
              borderRadius: '6px',
              backgroundColor: activeCount >= maxActiveSSIDs ? '#FEE2E2' : '#F2F8FD',
              height: '24px',
              px: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}
          >
            <Typography 
              component="span" 
              sx={{ 
                fontSize: 10, 
                fontWeight: 500, 
                color: activeCount >= maxActiveSSIDs ? '#DC2626' : 'primary.main' 
              }}
            >
              {activeCount} {t('event_ssids.active_count')}
            </Typography>
          </Box>
        </Stack>
        <Stack direction={'row'} gap={2} alignItems="center">
          <StatusFilter />
          <Button
            disabled={Boolean(user?.demo_mode)}
            size={'small'}
            variant={'contained'}
            onClick={() => {
              setSelection([])
              trackEvent('wifi_management_create')
              navigate('./new')
            }}
          >
            <i className={'fa-light fa-plus'} style={{ marginRight: '4px' }} />
            {t('event_ssids.actions.create')}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default Topbar